import { Component, OnInit, OnDestroy, inject, signal, computed, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';

import { environment } from '../../../../environments/environment';
import { FileDropZoneComponent } from '../file-drop-zone/file-drop-zone.component';
import { PdfThumbnailComponent } from '../pdf-thumbnail/pdf-thumbnail.component';
import { AuthService } from '../../../service/auth.service';
import { UsersService } from '../../../service/entity-services/users.service';
import { FileUploadService } from '../../../service/files-and-attachments/file-upload.service';
import {
  DocumentAnalysisService,
  IntakeAnalysisResponse,
  IntakeDocType,
  PartyMatch,
  AI_MAX_FILE_BYTES,
  AI_FILE_TOO_LARGE_MESSAGE,
  describeAnalysisFailure
} from '../../../service/ai/document-analysis.service';
import { WorkflowRequestsService } from '../../../service/entity-services/workflow-requests.service';
import { WorkflowRequest } from '../../../objects/workflow';

export type DialogMode = 'intake' | 'workflow';
export interface NewWorkflowRequestDialogData {
  mode?: DialogMode;
}

type ViewMode = 'idle' | 'thinking' | 'decision' | 'failed' | 'chatbot';

interface ManualTypeOption {
  docType: IntakeDocType;
  label: string;
}

interface DecisionCard {
  headline: string;
  reasoning: string;
  confidence: number;
  parties: { name: string; role: string; matched: boolean }[];
  effectiveDate: string;
  expirationDate: string;
  destinationLabel: string;
  destinationRoute: string;
  /** Extra detail lines for type-specific info */
  details: string[];
  /** When true, no intake form exists for this doc type yet */
  noIntakeAvailable: boolean;
  noIntakeMessage: string;
}

@Component({
  selector: 'app-new-workflow-request-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    FileDropZoneComponent,
    PdfThumbnailComponent
  ],
  templateUrl: './new-workflow-request-dialog.component.html',
  styleUrls: ['./new-workflow-request-dialog.component.css']
})
export class NewWorkflowRequestDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<NewWorkflowRequestDialogComponent>);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private authService = inject(AuthService);
  private usersService = inject(UsersService);
  private fileUploadService = inject(FileUploadService);
  private documentAnalysisService = inject(DocumentAnalysisService);
  private http = inject(HttpClient);
  private workflowRequestsService = inject(WorkflowRequestsService);
  private snackBar = inject(MatSnackBar);

  // Dialog mode comes from the opener (button @Input or workflow-item directly).
  // 'intake' = route to intake form, 'workflow' = create a workflow request record.
  private dialogData = inject<NewWorkflowRequestDialogData>(MAT_DIALOG_DATA, { optional: true }) || {};
  mode: DialogMode = this.dialogData.mode || 'intake';

  // Workflow creation state (only used when mode === 'workflow')
  workflowSaving = signal<boolean>(false);
  uploadedS3Url = signal<string | null>(null);

  // Requestor identity
  requestorName = signal<string>('');
  requestorEmail = signal<string>('');
  requestorInitial = computed(() => {
    const n = this.requestorName() || this.requestorEmail();
    return (n ? n.charAt(0) : '?').toUpperCase();
  });

  // View state machine
  view = signal<ViewMode>('idle');
  previousView = signal<ViewMode>('idle');

  // File the user dropped
  selectedFile = signal<File | null>(null);

  // Streaming "thinking" effect
  thinkingLines = signal<string[]>([]);
  private thinkingTimers: any[] = [];

  // AI result
  recommendation = signal<IntakeDocType>('contract');
  decisionCard = signal<DecisionCard | null>(null);
  aiResponse = signal<IntakeAnalysisResponse | null>(null);

  // Failure state. When analysis fails the user is told so plainly and picks the
  // document type themselves. There used to be a "demo mode" here that guessed the
  // type from the filename and showed placeholder parties and dates as if the AI
  // had read the file — never acceptable in front of a real customer.
  failureMessage = signal<string>('');
  failureReference = signal<string>('');
  failureCanRetry = signal<boolean>(true);

  // Intake mode can only route to forms that exist; workflow mode records any type.
  readonly manualTypeOptions: ManualTypeOption[] = this.mode === 'workflow'
    ? [
        { docType: 'contract', label: 'Contract / Agreement' },
        { docType: 'baa', label: 'Business Associate Agreement' },
        { docType: 'coi', label: 'Certificate of Insurance' },
        { docType: 'addendum', label: 'Addendum / Amendment' },
        { docType: 'unknown', label: 'Something else' }
      ]
    : [
        { docType: 'contract', label: 'Contract / Agreement' },
        { docType: 'baa', label: 'Business Associate Agreement' },
        { docType: 'coi', label: 'Certificate of Insurance' }
      ];

  // Chatbot
  @ViewChild('chatMessagesContainer') chatMessagesContainer?: ElementRef<HTMLDivElement>;
  chatMessages = signal<{ role: 'user' | 'bot'; content: string }[]>([]);
  chatInput = signal<string>('');
  chatLoading = signal<boolean>(false);

  async ngOnInit(): Promise<void> {
    try {
      const email = await this.authService.getUserEmail();
      this.requestorEmail.set(email ?? '');
      if (email) {
        const name = this.usersService.getUserDisplayNameByEmail(email);
        this.requestorName.set(name && name !== email ? name : '');
      }
    } catch (e) {
      console.warn('[NewWorkflowRequestDialog] failed to fetch user', e);
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  // ----- File drop -----

  onFileSelected(file: File): void {
    this.selectedFile.set(file);
    this.startReveal(file);
  }

  // ----- The reveal sequence -----

  private async startReveal(file: File, force = false): Promise<void> {
    this.view.set('thinking');
    this.thinkingLines.set([]);
    this.failureMessage.set('');
    this.failureReference.set('');
    this.clearTimers();

    // Start the scripted "thinking" animation immediately for visual feedback
    this.startThinkingAnimation();

    // Too big to be worth sending to the AI. Still upload it: a workflow request
    // records the file, and the user can pick the type themselves.
    if (file.size > AI_MAX_FILE_BYTES) {
      try {
        if (!this.uploadedS3Url()) this.uploadedS3Url.set(await this.uploadForAnalysis(file));
        this.showFailure(AI_FILE_TOO_LARGE_MESSAGE, undefined, false);
      } catch (err) {
        console.error('[NewWorkflowRequestDialog] upload failed', err);
        this.showFailure("We couldn't upload this file. Check your connection and try again.");
      }
      return;
    }

    try {
      const response = await this.runRealAnalysis(file, force);
      this.clearTimers();

      if (response && response.status === 'complete' && response.extraction) {
        // Real AI succeeded
        this.aiResponse.set(response);
        const docType = response.classification || response.extraction?.classification?.docType || 'unknown';
        this.recommendation.set(docType);
        this.decisionCard.set(this.buildCardFromResponse(response, docType));

        // Show any remaining thinking lines instantly, then reveal
        this.thinkingLines.set([
          'Reading header…',
          'Identifying parties…',
          'Checking signatures…',
          'Looking for matches in your records…',
          this.recognitionLineFor(docType),
          'Done.'
        ]);
        this.view.set('decision');
        this.cdr.detectChanges();
      } else {
        // errorMessage is for support/logs and can be raw AWS text; show the mapped message.
        console.warn('[NewWorkflowRequestDialog] AI analysis did not complete', response?.errorCode, response?.errorMessage);
        const failure = describeAnalysisFailure(response?.errorCode);
        this.showFailure(failure.message, response?.analysisId, failure.canRetry);
      }
    } catch (err) {
      // triggerIntakeAnalysis never throws (it maps errors to status 'failed'),
      // so anything landing here is the upload itself.
      console.error('[NewWorkflowRequestDialog] upload failed', err);
      this.showFailure("We couldn't upload this file. Check your connection and try again.");
    }
  }

  private async runRealAnalysis(file: File, force: boolean): Promise<IntakeAnalysisResponse | null> {
    // Step 1: Upload to S3. A retry reuses the copy already uploaded.
    let s3Url = this.uploadedS3Url();
    if (!s3Url) {
      s3Url = await this.uploadForAnalysis(file);
      this.uploadedS3Url.set(s3Url);
    }

    // Step 2: Trigger intake analysis with progress callback
    const progressCallback = (progress: { message: string }) => {
      this.addThinkingLine(progress.message);
    };

    return firstValueFrom(
      this.documentAnalysisService.triggerIntakeAnalysis(s3Url, file.name, progressCallback, force)
    );
  }

  private async uploadForAnalysis(file: File): Promise<string> {
    const fileExtension = file.name.split('.').pop();
    const uniqueFileName = `intake/${Date.now()}_${Math.random().toString(36).substring(2, 15)}.${fileExtension}`;
    const preSignedUrlResponse = await firstValueFrom(
      this.fileUploadService.getPresignedUrl(uniqueFileName)
    );
    const preSignedUrl = preSignedUrlResponse.presignedUrl;
    await firstValueFrom(this.fileUploadService.uploadFileToS3(preSignedUrl, file));
    return preSignedUrl.split('?')[0];
  }

  // ----- Failure: say so, then let the user choose the type -----

  private showFailure(message: string, analysisId?: string, canRetry = true): void {
    this.clearTimers();
    this.failureCanRetry.set(canRetry);
    this.aiResponse.set(null);
    this.decisionCard.set(null);
    this.failureMessage.set(message);
    this.failureReference.set(analysisId || '');
    // Drop the scripted lines ("Checking signatures…") — they describe work that
    // didn't produce anything, and leaving them up reads as if it had.
    this.thinkingLines.set([]);
    this.view.set('failed');
    this.cdr.detectChanges();
  }

  retryAnalysis(): void {
    const file = this.selectedFile();
    if (!file) return;
    // force=true: the backend dedupes by file hash and would otherwise hand back
    // the same cached failure.
    this.startReveal(file, true);
  }

  /**
   * The user tells us the type after a failed analysis. Intake mode opens that
   * form with the file and no AI results; workflow mode records the request.
   */
  chooseTypeManually(docType: IntakeDocType): void {
    this.recommendation.set(docType);
    if (this.mode === 'workflow') {
      this.createWorkflowRequest();
      return;
    }
    const route = this.intakeRouteFor(docType);
    if (route) this.navigateToIntake(route);
  }

  /** Workflow records need the uploaded file; without it, retrying is the only path. */
  canChooseManually(): boolean {
    return this.mode === 'intake' || !!this.uploadedS3Url();
  }

  private intakeRouteFor(docType: IntakeDocType): string {
    switch (docType) {
      case 'contract': return '/securehome/new-contract';
      case 'baa': return '/securehome/new-baa-full-page';
      case 'coi': return '/securehome/new-coi-full-page';
      default: return '';
    }
  }

  // ----- Thinking animation -----

  private startThinkingAnimation(): void {
    const script = [
      'Uploading document…',
      'Reading header…',
      'Identifying parties…',
      'Checking signatures…',
      'Looking for matches in your records…'
    ];
    let cumulative = 0;
    const stepMs = 800;

    script.forEach(line => {
      const t = setTimeout(() => {
        this.addThinkingLine(line);
      }, cumulative);
      this.thinkingTimers.push(t);
      cumulative += stepMs;
    });
  }

  private addThinkingLine(line: string): void {
    this.thinkingLines.update(arr => {
      if (arr.includes(line)) return arr;
      return [...arr, line];
    });
    this.cdr.detectChanges();
  }

  private clearTimers(): void {
    this.thinkingTimers.forEach(t => clearTimeout(t));
    this.thinkingTimers = [];
  }

  private recognitionLineFor(docType: IntakeDocType): string {
    switch (docType) {
      case 'baa': return 'Recognized: Business Associate Agreement.';
      case 'coi': return 'Recognized: Certificate of Insurance.';
      case 'addendum': return 'Recognized: Addendum / Amendment.';
      case 'unknown': return 'Could not determine document type.';
      default: return 'Recognized: Contract / Agreement.';
    }
  }

  // ----- Build decision card from real AI response -----

  private buildCardFromResponse(response: IntakeAnalysisResponse, docType: IntakeDocType): DecisionCard {
    const ext = response.extraction!;
    const classification = ext.classification;

    const parties = (ext.parties || []).map(p => {
      const match = (response.partyMatches || []).find(
        m => m.extractedName === p.name && m.matchConfidence !== 'none'
      );
      return { name: p.name, role: p.role, matched: !!match };
    });

    const effectiveDate = ext.effectiveDate?.normalized || ext.effectiveDate?.extracted || '';
    const expirationDate = ext.expirationDate?.normalized || ext.expirationDate?.extracted || '';

    const base: DecisionCard = {
      headline: this.headlineFor(docType),
      reasoning: classification?.reasoning || '',
      confidence: classification?.confidence || 0,
      parties,
      effectiveDate,
      expirationDate,
      destinationLabel: '',
      destinationRoute: '',
      details: [],
      noIntakeAvailable: false,
      noIntakeMessage: ''
    };

    switch (docType) {
      case 'contract':
        base.destinationLabel = 'Open Contract intake';
        base.destinationRoute = '/securehome/new-contract';
        if (ext.contractFields?.agreementType) {
          base.details.push(`Agreement type: ${ext.contractFields.agreementType}`);
        }
        if (ext.contractFields?.renewalType) {
          base.details.push(`Renewal: ${ext.contractFields.renewalType}`);
        }
        break;

      case 'baa':
        base.destinationLabel = 'Open BAA intake';
        base.destinationRoute = '/securehome/new-baa-full-page';
        if (ext.baaFields?.coveredEntity) {
          base.details.push(`Covered Entity: ${ext.baaFields.coveredEntity}`);
        }
        if (ext.baaFields?.businessAssociate) {
          base.details.push(`Business Associate: ${ext.baaFields.businessAssociate}`);
        }
        if (ext.baaFields?.typesOfService?.length) {
          base.details.push(`Services: ${ext.baaFields.typesOfService.join(', ')}`);
        }
        break;

      case 'coi':
        base.destinationLabel = 'Open COI intake';
        base.destinationRoute = '/securehome/new-coi-full-page';
        if (ext.coiFields?.insuranceProvider) {
          base.details.push(`Insurer: ${ext.coiFields.insuranceProvider}`);
        }
        if (ext.coiFields?.coverageTypes?.length) {
          base.details.push(`Coverage: ${ext.coiFields.coverageTypes.join(', ')}`);
        }
        if (ext.coiFields?.policyNumber) {
          base.details.push(`Policy #: ${ext.coiFields.policyNumber}`);
        }
        break;

      case 'addendum':
        base.destinationLabel = '';
        base.destinationRoute = '';
        base.noIntakeAvailable = true;
        base.noIntakeMessage = 'Addendum intake is not yet available. You can attach this document to an existing contract.';
        if (ext.addendumFields?.parentDocumentReference) {
          base.details.push(`Amends: ${ext.addendumFields.parentDocumentReference}`);
        }
        if (ext.addendumFields?.amendmentSummary) {
          base.details.push(ext.addendumFields.amendmentSummary);
        }
        break;

      case 'unknown':
        base.destinationLabel = '';
        base.destinationRoute = '';
        base.noIntakeAvailable = true;
        base.noIntakeMessage = "I couldn't determine the document type. Try describing it using the AI assistant.";
        break;
    }

    if (ext.summary) {
      base.details.unshift(ext.summary);
    }

    return base;
  }

  private headlineFor(docType: IntakeDocType): string {
    switch (docType) {
      case 'contract': return 'I found a Contract / Agreement';
      case 'baa': return 'I found a Business Associate Agreement';
      case 'coi': return 'I found a Certificate of Insurance';
      case 'addendum': return 'I found an Addendum / Amendment';
      case 'unknown': return 'I could not classify this document';
    }
  }

  // ----- View file -----

  viewFile(): void {
    const file = this.selectedFile();
    if (!file) return;
    const blobUrl = URL.createObjectURL(file);
    window.open(blobUrl, '_blank');
  }

  // ----- Decision card actions -----

  acceptRecommendation(): void {
    if (this.mode === 'workflow') {
      this.createWorkflowRequest();
    } else {
      this.routeToIntake();
    }
  }

  private routeToIntake(): void {
    const file = this.selectedFile();
    const card = this.decisionCard();
    if (!file || !card || card.noIntakeAvailable) return;
    this.navigateToIntake(card.destinationRoute);
  }

  private navigateToIntake(route: string): void {
    const file = this.selectedFile();
    if (!file) return;
    const analysis = this.aiResponse();

    this.router.navigate([route], {
      state: {
        prefilledFile: file,
        intakeAnalysisResult: analysis,
        // No analysis means it already failed here; the form must not start a
        // second run of the same failing analysis on its own.
        skipAi: !analysis,
        source: 'workflow-request'
      }
    });
    this.dialogRef.close({ routed: this.recommendation() });
  }

  /**
   * Workflow mode path: instead of navigating to an intake form, create a
   * WorkflowRequest record. AssignedTo is auto-routed by the AI's docType
   * via environment.workflowRouting.
   */
  private async createWorkflowRequest(): Promise<void> {
    const file = this.selectedFile();
    const ai = this.aiResponse();
    if (!file || this.workflowSaving()) return;

    this.workflowSaving.set(true);

    const docType = this.recommendation();

    // Assign to the person who created the request.
    //
    // This replaced the old `environment.workflowRouting[docType]` lookup, whose
    // per-org values were never configured — every entry in every org was the
    // placeholder 'admin@example.com', and it had already been written into real
    // records. Since the backend has no DeleteItem, those rows are permanent, so
    // there is no version of "route to the configured admin" that is safe to keep
    // until the addresses are real. The creator is always a genuine, meaningful
    // owner, and the request can be handed on with the Assignee picker on the
    // edit form.
    const assignedTo = this.requestorEmail() || null;

    const ext = ai?.extraction;
    const card = this.decisionCard();

    const payload: Partial<WorkflowRequest> = {
      name: this.suggestedWorkflowName(card, file.name),
      documentType: docType,
      transactionType: 'New',
      priority: 'Normal',
      requestor: this.requestorEmail() || 'unknown@example.com',
      requestorName: this.requestorName() || this.requestorEmail() || '',
      assignedTo,
      s3FileUrl: this.uploadedS3Url() || '',
      fileName: file.name,
      classification: docType,
      classificationConfidence: ext?.classification?.confidence ?? card?.confidence ?? 0,
      extractedSummary: ext?.summary || '',
      extractedParties: (ext?.parties as any) || [],
      extractedEffectiveDate: ext?.effectiveDate?.normalized || null,
      extractedExpirationDate: ext?.expirationDate?.normalized || null,
      intakeAnalysisResult: ai || null
    };

    try {
      await firstValueFrom(this.workflowRequestsService.create(payload));
      this.snackBar.open('Workflow request submitted', 'OK', { duration: 3500 });
      this.dialogRef.close({ submitted: true });
      this.router.navigate(['/securehome/workflows']);
    } catch (err) {
      // Error snackbar already surfaced by the service
      console.error('[Dialog] createWorkflowRequest failed:', err);
    } finally {
      this.workflowSaving.set(false);
      this.cdr.detectChanges();
    }
  }

  private suggestedWorkflowName(card: DecisionCard | null, fallback: string): string {
    if (!card) return fallback;
    const firstParty = card.parties?.[0]?.name;
    const headline = card.headline.replace(/^I found (a |an )?/i, '');
    if (firstParty) return `${headline} — ${firstParty}`;
    return headline;
  }


  notWhatIExpected(): void {
    this.openChatbot();
  }

  startOver(): void {
    this.clearTimers();
    this.selectedFile.set(null);
    this.thinkingLines.set([]);
    this.decisionCard.set(null);
    this.aiResponse.set(null);
    this.uploadedS3Url.set(null);
    this.failureMessage.set('');
    this.failureReference.set('');
    this.view.set('idle');
  }

  // ----- Chatbot -----

  openChatbot(): void {
    this.previousView.set(this.view());
    // Seed the greeting if this is the first time opening
    if (this.chatMessages().length === 0) {
      const name = this.requestorName();
      this.chatMessages.set([{
        role: 'bot',
        content: `Hi${name ? ', ' + name : ''}! Tell me about what you're sending and I'll help you figure out what to do with it.`
      }]);
    }
    this.view.set('chatbot');
  }

  backFromChatbot(): void {
    this.view.set(this.previousView() || 'idle');
  }

  async sendChatMessage(): Promise<void> {
    const msg = this.chatInput().trim();
    if (!msg || this.chatLoading()) return;

    this.chatMessages.update(arr => [...arr, { role: 'user', content: msg }]);
    this.chatInput.set('');
    this.chatLoading.set(true);
    this.cdr.detectChanges();
    this.scrollChatToBottom();

    try {
      const chatUrl = (environment as any).URL_INTAKE_CHAT;
      if (!chatUrl) {
        this.chatMessages.update(arr => [...arr, {
          role: 'bot',
          content: 'Chat is not yet configured. The backend endpoint needs to be deployed.'
        }]);
        return;
      }

      const messagesForApi = this.chatMessages()
        .filter(m => m.role === 'user' || m.role === 'bot')
        .map(m => ({ role: m.role === 'bot' ? 'assistant' : 'user', content: m.content }));

      const fileContext = this.aiResponse() ? {
        classification: this.aiResponse()?.classification,
        extraction: {
          summary: this.aiResponse()?.extraction?.summary,
          parties: this.aiResponse()?.extraction?.parties,
          classification: this.aiResponse()?.extraction?.classification
        },
        fileName: this.selectedFile()?.name
      } : null;

      const response = await firstValueFrom(
        this.http.post<{ reply: string }>(chatUrl, { messages: messagesForApi, fileContext })
      );

      this.chatMessages.update(arr => [...arr, { role: 'bot', content: response.reply }]);
    } catch (err) {
      console.error('[Dialog Chat] Error:', err);
      this.chatMessages.update(arr => [...arr, {
        role: 'bot',
        content: "Sorry, I couldn't process that right now. Please try again."
      }]);
    } finally {
      this.chatLoading.set(false);
      this.cdr.detectChanges();
      this.scrollChatToBottom();
    }
  }

  private scrollChatToBottom(): void {
    setTimeout(() => {
      const el = this.chatMessagesContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }

  // ----- Cancel -----

  onCancel(): void {
    this.clearTimers();
    this.dialogRef.close(null);
  }
}
